import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Button, Form, Row, Col, Pagination } from 'react-bootstrap';
import ViewItemServiceCardModule from './ViewItemServiceCardModule';
import AddItemServiceCardModule from './AddItmeServiceCardModule'; // Fixed typo in import
import UpdateItemServiceCardModule from './UpdateItemServiceCardModule';
import CollapseHeader from '../../../../../core/common/collapse-header';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { showError } from '../../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../../router/all_routes';
import { debounce } from 'lodash';
import { checkPermission } from '../../../../../core/permissons/CheckPermissons';

// Navigation items with corrected routes
const navItems = [
  { label: 'Item Services', icon: 'ti ti-tools', link: all_routes.itemServiceCards, permission: 'ROLE_GET_ITEM_SERVICE_CARD' },
  { label: 'Items', icon: 'ti ti-box', link: all_routes.items, permission: 'ROLE_ADD_ITEM' },
  { label: 'Item Type & Brand', icon: 'ti ti-plus', link: all_routes.additemtypeandbrand, permission: 'ROLE_ADD_ITEM' },
];

const C = {
  card: { padding: 24, borderRadius: 8, background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' } as React.CSSProperties,
  hover: { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' } as React.CSSProperties,
  title: { fontSize: 16, fontWeight: 600, marginBottom: 12 } as React.CSSProperties,
  pillRow: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' } as React.CSSProperties,
  pill: { padding: '4px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  line: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 } as React.CSSProperties,
  icon: { width: 24, height: 24, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties,
  hr: { margin: '12px 0', borderColor: '#e5e7eb' } as React.CSSProperties,
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 } as React.CSSProperties,
  idText: { fontSize: 12, color: '#64748B' } as React.CSSProperties,
  actions: { display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
  aBtn: {
    width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
  } as React.CSSProperties,
};

// Updated interface with itemTypeId
export interface ItemServiceCardDTO {
  id: number;
  comment?: string;
  itemId: number;
  itemTypeId: number;
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  assignedUserId?: number;
  accountablePersonId?: number | null;
  serviceDetails?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

/** ---- Status helpers (normalize + classes) ---- */
type Status = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
type StatusChip = 'ALL' | Status;
const KNOWN_STATUSES: Status[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'EXPIRED'];

const normalizeStatus = (raw?: string | null): Status => {
  if (!raw) return 'PENDING';
  const k = raw.toString().trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (k === 'INPROGRESS') return 'IN_PROGRESS';
  if (k === 'CANCELED') return 'CANCELLED';
  return (KNOWN_STATUSES.includes(k as Status) ? (k as Status) : 'PENDING');
};

const statusClasses = (s: Status) => {
  switch (s) {
    case 'PENDING':     return { bg: 'bg-secondary', text: 'text-white' };
    case 'IN_PROGRESS': return { bg: 'bg-info',      text: 'text-white' };
    case 'COMPLETED':   return { bg: 'bg-success',   text: 'text-white' };
    case 'CANCELLED':   return { bg: 'bg-danger',    text: 'text-white' };
    case 'EXPIRED':     return { bg: 'bg-warning',   text: 'text-dark'  }; // better contrast on warning
    default:            return { bg: 'bg-secondary', text: 'text-white' };
  }
};

const ItemServiceCard: FC = () => {
  const navigate = useNavigate();
  const [itemServiceCards, setItemServiceCards] = useState<ItemServiceCardDTO[]>([]);
  const [selectedItemServiceCard, setSelectedItemServiceCard] = useState<ItemServiceCardDTO | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusChip, setStatusChip] = useState<StatusChip>('ALL');
  const [hover, setHover] = useState<number | null>(null);

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Recompute each render so late-loaded permissions don't freeze tabs
  const visibleNavItems = navItems.filter(it => !it.permission || checkPermission(it.permission));

  const checkToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return true;
  }, [navigate]);

  const fetchItemServiceCards = useCallback(async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    try {
      const payload = {
        date: null,
        from: fromDate || null,
        to: toDate || null,
        status: statusChip === 'ALL' ? '' : statusChip,
      };
      const response = await apiDefinitions.getAllItemServiceCards(currentPage, payload);
      if (response?.data?.status === 'S1000') {
        const itemServiceCardsData: ItemServiceCardDTO[] = response.data.data || [];
        setItemServiceCards(itemServiceCardsData);
        const total = response.data.total || 0;
        setTotalPages(Math.max(1, Math.ceil(total / 10)));
        setTotalCount(total);
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch item service cards');
        setItemServiceCards([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error fetching item service cards');
        setItemServiceCards([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [checkToken, currentPage, fromDate, toDate, statusChip, navigate]);

  const debouncedFetch = useCallback(debounce(fetchItemServiceCards, 400), [fetchItemServiceCards]);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch, currentPage, fromDate, toDate, statusChip]);

  const handleOpenView = useCallback(async (id: number) => {
    if (!checkToken()) return;
    try {
      const response = await apiDefinitions.getItemServiceCardById(id);
      if (response?.data?.status === 'S1000') {
        setSelectedItemServiceCard(response.data.data);
        setShowViewModal(true);
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch item service card');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error fetching item service card');
      }
    }
  }, [checkToken, navigate]);

  const handleDelete = useCallback(async (id: number) => {
    if (!checkToken()) return;
    try {
      const response = await apiDefinitions.deleteItemServiceCard(id);
      if (response?.data?.status === 'S1000') {
        setItemServiceCards((prev) => prev.filter((isc) => isc.id !== id));
        // keeping showError to preserve your alert pattern; replace with showSuccess if you have it
        showError('Item service card deleted successfully');
      } else {
        showError(response?.data?.statusDescription || 'Failed to delete item service card');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError(error?.response?.data?.statusDescription || 'Error deleting item service card');
      }
    }
  }, [checkToken, navigate]);

  /** --- Precompute counts by normalized status for chips --- */
  const countsByStatus = useMemo(() => {
    const counts: Record<Status, number> = {
      PENDING: 0, IN_PROGRESS: 0, COMPLETED: 0, CANCELLED: 0, EXPIRED: 0
    };
    for (const isc of itemServiceCards) {
      counts[normalizeStatus(isc.status)]++;
    }
    return counts;
  }, [itemServiceCards]);

  /** --- Card item component --- */
  const ItemServiceCardItem: FC<{ isc: ItemServiceCardDTO }> = ({ isc }) => {
    const effStatus = normalizeStatus(isc.status);
    const cls = statusClasses(effStatus);

    // Completed cards: no edit or delete
    const isCompleted = effStatus === 'COMPLETED';
    const canEdit = !isCompleted && checkPermission?.('ROLE_MANAGE_SERVICE_CARD');
    const canDelete = !isCompleted && checkPermission?.('ROLE_MANAGE_SERVICE_CARD');

    return (
      <div
        style={{ ...C.card, ...(hover === isc.id ? C.hover : {}) }}
        onMouseEnter={() => setHover(isc.id)}
        onMouseLeave={() => setHover(null)}
      >
        <h5 style={C.title}>{isc.comment || `Service Card #${isc.id}`}</h5>

        <div style={C.pillRow}>
          <span className={`${cls.bg} ${cls.text} fw-bold`} style={C.pill} title={effStatus}>
            {effStatus.replace('_', ' ')}
          </span>
          <span className="bg-info-transparent text-info" style={C.pill}>Item #{isc.itemId}</span>
          <span className="bg-primary-transparent text-primary" style={C.pill}>Type #{isc.itemTypeId}</span>
        </div>

        <div style={C.line}>
          <span className="bg-success-transparent text-success" style={C.icon}><i className="ti ti-calendar" /></span>
          <span title={`${isc.scheduledDateTime || '-'}${isc.scheduledEndDateTime ? ' → ' + isc.scheduledEndDateTime : ''}`}>
            {isc.scheduledDateTime || '-'}
          </span>
        </div>

        <div style={C.line}>
          <span className="bg-dark-transparent text-dark" style={C.icon}><i className="ti ti-user-shield" /></span>
          <span>{isc.accountablePersonId ?? '-'}</span>
        </div>

        <hr style={C.hr} />

        <div style={C.footer}>
          <span style={C.idText}>ID: {isc.id}</span>
          <div style={C.actions}>
            {/* View is always available */}
            <button
              className="bg-info text-white"
              style={C.aBtn}
              title="View"
              onClick={() => handleOpenView(isc.id)}
            >
              <i className="ti ti-eye" />
            </button>

            {/* Edit shown only when NOT completed (and passes permission if used) */}
            {canEdit !== false && !isCompleted && (
              <button
                className="bg-warning text-white"
                style={C.aBtn}
                title="Edit"
                onClick={() => { setSelectedItemServiceCard(isc); setShowUpdateModal(true); }}
              >
                <i className="ti ti-pencil" />
              </button>
            )}

            {/* Delete shown only when NOT completed (and passes permission if used) */}
            {canDelete !== false && !isCompleted && (
              <button
                className="bg-danger text-white"
                style={C.aBtn}
                title="Delete"
                onClick={() => handleDelete(isc.id)}
              >
                <i className="ti ti-trash" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Facility Maintenance/Item Service/Item Services</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons"><CollapseHeader /></div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {visibleNavItems.length ? (
                    visibleNavItems.map((item, index) => (
                      <li className="nav-item me-item" key={index}>
                        <NavLink
                          to={item.link}
                          className={({ isActive }) => `nav-link px-0 ${isActive ? 'active' : ''}`}
                          end
                        >
                          <i className={item.icon}></i> {item.label}
                        </NavLink>
                      </li>
                    ))
                  ) : (
                    <li className="nav-item text-muted px-2 py-2">No tabs available</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Filters + New */}
            <div className="card">
              <div className="card-body">
                <Row className="g-2 align-items-end flex-nowrap">
                  <Col xs="auto">
                    <Form.Group className="mb-0">
                      <Form.Label className="mb-1">From</Form.Label>
                      <Form.Control
                        type="date"
                        value={fromDate || ''}
                        onChange={(e) => setFromDate(e.target.value || '')}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs="auto">
                    <Form.Group className="mb-0">
                      <Form.Label className="mb-1">To</Form.Label>
                      <Form.Control
                        type="date"
                        value={toDate || ''}
                        onChange={(e) => setToDate(e.target.value || '')}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs="auto" className="ms-auto">
                    <Button variant="success" onClick={() => setShowAddModal(true)}>
                      + New Item Service Card
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Status Chip Bar */}
            <div className="card mt-2">
              <div className="card-body d-flex flex-wrap gap-2 align-items-center">
                {(['ALL', 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'EXPIRED'] as StatusChip[]).map((chip) => (
                  <button
                    key={chip}
                    className={`d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-bold ${statusChip === chip ? 'bg-primary text-white' : 'bg-light'}`}
                    onClick={() => setStatusChip(chip)}
                  >
                    {chip.replace('_', ' ')} (
                      {chip === 'ALL'
                        ? itemServiceCards.length
                        : (countsByStatus[chip as Status] || 0)
                      }
                    )
                  </button>
                ))}
              </div>
            </div>

            {/* Cards Grid */}
            <div className="card">
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center my-4">
                    <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 16
                    }}
                  >
                    {itemServiceCards
                      .filter((isc) => statusChip === 'ALL' || normalizeStatus(isc.status) === statusChip)
                      .map((isc) => (
                        <ItemServiceCardItem key={isc.id} isc={isc} />
                      ))}
                  </div>
                )}

                {/* Pagination */}
                <div className="d-flex justify-content-end mt-3">
                  <Pagination>
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <Pagination.Item
                        key={idx + 1}
                        active={idx === currentPage}
                        onClick={() => setCurrentPage(idx)}
                      >
                        {idx + 1}
                      </Pagination.Item>
                    ))}
                  </Pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showViewModal && selectedItemServiceCard && (
        <ViewItemServiceCardModule
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          onSuccess={fetchItemServiceCards}
          itemServiceCard={selectedItemServiceCard}
        />
      )}

      <AddItemServiceCardModule
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchItemServiceCards}
      />

      {showUpdateModal && selectedItemServiceCard && (
        <UpdateItemServiceCardModule
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          onSuccess={fetchItemServiceCards}
          itemServiceCard={selectedItemServiceCard}
        />
      )}
    </div>
  );
};

export default ItemServiceCard;
